import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

/**
 * Phase 2.9 Tier 2 — Visual Approval Workflow Builder.
 *
 * Three-pane layout:
 *   - Left palette  ("palette")  — drag step types onto the canvas
 *                                   (Approval Step, Notification — Tier 2
 *                                   single-track only).
 *   - Center canvas ("canvas")    — ordered list of step cards. SortableJS
 *                                   reorder; click a card to select it.
 *   - Right rail   ("rail")       — selected step's details (name, voting
 *                                   rule, SLA, approver pool, conditions
 *                                   chip builder, escalation panel).
 *
 * Live preview:
 *   - Below the canvas, a small "(amount, project_phase, fund_source_id,
 *     vendor_company_id)" form. Debounced 600ms POST to
 *     /company_settings/approval_workflows/:id/preview returning the
 *     resolved step-by-step routing.
 *
 * Server endpoints:
 *   - GET    builder_data     — field manifest + pool catalog (on connect)
 *   - POST   approval_steps   — add a step (palette drag)
 *   - PATCH  approval_steps/:id — save a step's edits (right rail)
 *   - DELETE approval_steps/:id — remove a step
 *   - PATCH  approval_steps/reorder — drag-to-reorder
 *   - POST   workflows/:id/preview  — live routing preview
 *
 * Wiring back to other Tier 2 Stimulus surfaces:
 *   - This controller dispatches `workflow:step-changed` on step
 *     create/update/delete; the live-preview pane re-runs on each.
 *   - The conditions chip UI lives inline (no nested controller needed
 *     — it's tightly coupled to the right-rail state).
 *   - The approver-pool picker modal is mounted at the page root and
 *     listens for `pool-picker:open`; it dispatches
 *     `pool-picker:selected` back when the admin picks a pool.
 */
export default class extends Controller {
  static targets = ["palette", "canvas", "rail", "previewBody", "previewInputs"]

  static values = {
    workflowId: Number,
    builderDataUrl: String,
    stepsUrl: String,
    reorderUrl: String,
    previewUrl: String
  }

  connect() {
    this._registry = { condition_fields: [], pools: [], voting_rules: [], escalation_kinds: [] }
    this._selectedStepId = null
    this._steps = this._readInitialSteps()
    this._previewTimer = null
    this._sortable = null

    this._loadRegistry().then(() => {
      this._renderCanvas()
      this._initSortables()
      this._renderRail()
      this._schedulePreview(0)
    })

    document.addEventListener("pool-picker:selected", this._onPoolPicked = this._onPoolPicked.bind(this))
  }

  disconnect() {
    if (this._previewTimer) clearTimeout(this._previewTimer)
    if (this._sortable) this._sortable.destroy()
    document.removeEventListener("pool-picker:selected", this._onPoolPicked)
  }

  // ── Bootstrapping ──────────────────────────────────────────────

  _readInitialSteps() {
    const raw = this.element.dataset.workflowBuilderInitialSteps
    if (!raw) return []
    try { return JSON.parse(raw) } catch (_) { return [] }
  }

  async _loadRegistry() {
    if (!this.builderDataUrlValue) return
    try {
      const r = await fetch(this.builderDataUrlValue, { headers: { Accept: "application/json" } })
      const j = await r.json()
      if (j.success) this._registry = j
    } catch (e) {
      console.warn("[workflow-builder] registry load failed", e)
    }
  }

  // ── Canvas: render + sort ──────────────────────────────────────

  _renderCanvas() {
    if (!this.hasCanvasTarget) return
    if (!this._steps.length) {
      this.canvasTarget.innerHTML = `
        <div class="text-center text-[13px] text-gray-400 py-10 border-2 border-dashed border-gray-200 rounded-lg">
          Drag a step from the palette, or click <em>Add step</em>.
        </div>`
      return
    }

    this.canvasTarget.innerHTML = this._steps.map((s, idx) => this._renderStepCard(s, idx)).join("")
  }

  _renderStepCard(s, idx) {
    const selected = s.id === this._selectedStepId
    const poolName = s.approver_pool_id ? this._poolName(s.approver_pool_id) : "<em class=\"text-rose-600\">No pool</em>"
    const sla = s.sla_business_hours > 0 ? `${s.sla_business_hours}h SLA` : "no SLA"
    const escIcon = (s.escalation && s.escalation.kind) ? "⚡" : ""
    const condCount = (s.conditions || []).filter(c => c && c.field).length
    const condBadge = condCount > 0
      ? `<span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700">${condCount} condition${condCount === 1 ? "" : "s"}</span>`
      : ""

    return `
      <div class="step-card group bg-white border ${selected ? "border-blue-500 ring-1 ring-blue-200" : "border-gray-200"} rounded-lg p-3 mb-2 cursor-pointer hover:shadow-sm"
           data-step-id="${s.id}"
           data-action="click->workflow-builder#selectStep">
        <div class="flex items-center gap-2">
          <div class="cursor-grab text-gray-400 select-none drag-handle">⋮⋮</div>
          <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-700">${idx + 1}</span>
          <div class="flex-1 min-w-0">
            <div class="text-[13px] font-medium text-gray-900 truncate">${this._escape(s.name || "Untitled step")} ${condBadge}</div>
            <div class="text-[11px] text-gray-500 truncate">
              ${this._humanize(s.voting_rule)} · ${poolName} · ${sla} ${escIcon}
            </div>
          </div>
          <button type="button" class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-rose-600 px-1 cursor-pointer"
                  data-action="click->workflow-builder#removeStepClicked"
                  data-step-id="${s.id}"
                  title="Remove step">×</button>
        </div>
      </div>`
  }

  _initSortables() {
    if (this._sortable) { this._sortable.destroy(); this._sortable = null }
    if (!this.hasCanvasTarget || !this._steps.length) return

    this._sortable = Sortable.create(this.canvasTarget, {
      animation: 150,
      handle: ".drag-handle",
      ghostClass: "bg-blue-50",
      onEnd: () => {
        const ids = Array.from(this.canvasTarget.querySelectorAll("[data-step-id]"))
          .map(el => parseInt(el.dataset.stepId, 10))
          .filter(Boolean)
        // Reorder local state to match DOM
        const idIdx = new Map(ids.map((id, i) => [id, i]))
        this._steps.sort((a, b) => (idIdx.get(a.id) ?? 0) - (idIdx.get(b.id) ?? 0))
        this._steps.forEach((s, i) => { s.position = i + 1 })
        this._patchReorder(ids)
        this._renderCanvas()
        this._schedulePreview()
      }
    })
  }

  // ── Step actions ───────────────────────────────────────────────

  selectStep(event) {
    const id = parseInt(event.currentTarget.dataset.stepId, 10)
    this._selectedStepId = id
    this._renderCanvas()
    this._renderRail()
  }

  // Auto-name new steps so the admin doesn't get a browser `prompt()`
  // (per CLAUDE.md). They can rename inline in the right rail.
  async addStepClicked() {
    const position = this._steps.length + 1
    const name = `Step ${position}`
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const r = await fetch(this.stepsUrlValue, {
      method: "POST",
      headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrf || "" },
      body: JSON.stringify({ approval_step: {
        name, position, voting_rule: "all_required",
        sla_business_hours: 0, allow_email_approval: true,
        require_comment_on_reject: true
      } })
    })
    const j = await r.json().catch(() => ({}))
    if (j.success && j.step) {
      this._steps.push(j.step)
      this._selectedStepId = j.step.id
      this._renderCanvas()
      this._initSortables()
      this._renderRail()
      this._schedulePreview()
    } else {
      this._flashRail((j.errors || ["Failed to add step"]).join(", "), "error")
    }
  }

  // Two-click delete: first click flips the trash icon into a tiny
  // inline "Confirm" affordance; second click within 4 seconds
  // performs the delete. No browser confirm() per CLAUDE.md.
  removeStepClicked(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const id = parseInt(btn.dataset.stepId, 10)
    if (!id) return

    if (btn.dataset.confirming === "1") {
      this._removeStep(id)
      return
    }

    btn.dataset.confirming = "1"
    const original = btn.textContent
    btn.textContent = "Confirm"
    btn.classList.add("text-rose-700", "font-medium", "opacity-100")
    btn.classList.remove("opacity-0")
    btn._timer = setTimeout(() => {
      btn.dataset.confirming = ""
      btn.textContent = original
      btn.classList.remove("text-rose-700", "font-medium", "opacity-100")
    }, 4000)
  }

  async _removeStep(id) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const url = this.stepsUrlValue + "/" + id
    const r = await fetch(url, {
      method: "DELETE",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "" }
    })
    if (r.ok) {
      this._steps = this._steps.filter(s => s.id !== id)
      this._steps.forEach((s, i) => { s.position = i + 1 })
      if (this._selectedStepId === id) this._selectedStepId = null
      this._renderCanvas()
      this._initSortables()
      this._renderRail()
      this._schedulePreview()
    } else {
      this._flashRail("Failed to remove step", "error")
    }
  }

  async _patchReorder(ids) {
    if (!this.reorderUrlValue) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    await fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrf || "" },
      body: JSON.stringify({ step_ids: ids })
    })
  }

  // ── Right rail (selected step editor) ──────────────────────────

  _renderRail() {
    if (!this.hasRailTarget) return
    const step = this._steps.find(s => s.id === this._selectedStepId)
    if (!step) {
      this.railTarget.innerHTML = `
        <div class="text-[12px] text-gray-400 text-center py-12">
          Select a step on the canvas to configure it.
        </div>`
      return
    }

    const pool = step.approver_pool_id ? this._poolName(step.approver_pool_id) : "Unassigned"
    const fields = this._registry.condition_fields || []
    const votingRules = this._registry.voting_rules || ["all_required", "any_one", "majority", "at_least_n"]
    const escalationKinds = this._registry.escalation_kinds || ["notify", "reassign", "auto_approve_skip"]
    const escalation = step.escalation || {}
    const escKind = (escalation.kind || "")

    this.railTarget.innerHTML = `
      <div class="space-y-4">
        <div>
          <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">Step</div>
          <input type="text" value="${this._escapeAttr(step.name || "")}"
                 class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                 data-action="change->workflow-builder#updateStepField"
                 data-field="name" />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">Voting rule</div>
            <select class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand cursor-pointer"
                    data-action="change->workflow-builder#updateStepField"
                    data-field="voting_rule">
              ${votingRules.map(v => `<option value="${v}" ${v === step.voting_rule ? "selected" : ""}>${this._humanize(v)}</option>`).join("")}
            </select>
          </div>
          <div ${step.voting_rule === "at_least_n" ? "" : "class=\"hidden\""}>
            <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">N</div>
            <input type="number" min="1" value="${step.at_least_n || ""}"
                   class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                   data-action="change->workflow-builder#updateStepField"
                   data-field="at_least_n" />
          </div>
        </div>

        <div>
          <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">Approver pool</div>
          <div class="flex items-center gap-2">
            <div class="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-800">${pool}</div>
            <button type="button"
                    class="px-3 py-2 text-[12px] bg-white border border-gray-200 hover:bg-gray-50 rounded-lg cursor-pointer"
                    data-action="click->workflow-builder#openPoolPicker">Change</button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">SLA (business hrs)</div>
            <input type="number" min="0" value="${step.sla_business_hours || 0}"
                   class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                   data-action="change->workflow-builder#updateStepField"
                   data-field="sla_business_hours" />
            <div class="text-[10px] text-gray-400 mt-0.5">0 = no SLA</div>
          </div>
          <div class="flex items-end gap-2 text-[12px] text-gray-700">
            <label class="inline-flex items-center gap-1">
              <input type="checkbox" ${step.allow_email_approval ? "checked" : ""}
                     data-action="change->workflow-builder#updateStepField"
                     data-field="allow_email_approval" />
              Email approval
            </label>
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium">Escalation</div>
            <select class="text-[12px] border border-gray-200 rounded px-1.5 py-0.5 bg-white cursor-pointer"
                    data-action="change->workflow-builder#updateEscalationKind">
              <option value="">— none —</option>
              ${escalationKinds.map(k => `<option value="${k}" ${k === escKind ? "selected" : ""}>${this._humanize(k)}</option>`).join("")}
            </select>
          </div>
          ${this._renderEscalationSubFields(escKind, escalation)}
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <div class="text-[11px] uppercase tracking-wide text-gray-400 font-medium">Conditions</div>
            <button type="button"
                    class="text-[11px] px-2 py-0.5 bg-white border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
                    data-action="click->workflow-builder#addCondition">+ Condition</button>
          </div>
          <div data-workflow-builder-conditions>
            ${this._renderConditions(step, fields)}
          </div>
        </div>

        <div class="text-right">
          <button type="button"
                  class="px-3 py-1.5 text-[13px] bg-gray-900 text-white rounded-lg hover:bg-gray-700 cursor-pointer"
                  data-action="click->workflow-builder#saveStep">Save step</button>
        </div>
      </div>`
  }

  _renderEscalationSubFields(kind, escalation) {
    if (kind === "notify") {
      const ids = (escalation.notify_user_ids || []).join(",")
      return `<input type="text" value="${this._escapeAttr(ids)}"
                placeholder="comma-separated user ids"
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                data-action="change->workflow-builder#updateEscalationField"
                data-field="notify_user_ids" />`
    }
    if (kind === "reassign") {
      const targetId = escalation.target_pool_id || ""
      return `<select class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white cursor-pointer"
                data-action="change->workflow-builder#updateEscalationField"
                data-field="target_pool_id">
                <option value="">— select pool —</option>
                ${(this._registry.pools || []).map(p => `<option value="${p.id}" ${p.id == targetId ? "selected" : ""}>${this._escape(p.name)}</option>`).join("")}
              </select>`
    }
    if (kind === "auto_approve_skip") {
      return `<div class="text-[11px] text-gray-500">Auto-approve and skip on SLA breach.</div>`
    }
    return `<div class="text-[11px] text-gray-400">No escalation configured.</div>`
  }

  _renderConditions(step, fields) {
    const conditions = (step.conditions || []).filter(c => c)
    if (!conditions.length) {
      return `<div class="text-[11px] text-gray-400 italic">No conditions — step always runs.</div>`
    }

    return conditions.map((c, i) => {
      const fld = fields.find(f => f.key === c.field) || fields[0]
      const operators = (fld?.operators) || ["eq", "neq"]
      const valueInput = this._renderConditionValueInput(c, i, fld)
      return `
        <div class="flex items-center gap-1 mb-2">
          <span class="text-[10px] text-gray-400 w-3">${i + 1}</span>
          <select class="text-[12px] border border-gray-200 rounded px-1.5 py-1 bg-white cursor-pointer"
                  data-action="change->workflow-builder#updateConditionField"
                  data-cond-index="${i}">
            ${fields.map(f => `<option value="${f.key}" ${f.key === c.field ? "selected" : ""}>${this._escape(f.label)}</option>`).join("")}
          </select>
          <select class="text-[12px] border border-gray-200 rounded px-1.5 py-1 bg-white cursor-pointer"
                  data-action="change->workflow-builder#updateConditionOperator"
                  data-cond-index="${i}">
            ${operators.map(op => `<option value="${op}" ${op === c.operator ? "selected" : ""}>${this._operatorLabel(op)}</option>`).join("")}
          </select>
          ${valueInput}
          <button type="button" class="text-gray-400 hover:text-rose-600 px-1 cursor-pointer"
                  data-action="click->workflow-builder#removeCondition"
                  data-cond-index="${i}">×</button>
        </div>`
    }).join("")
  }

  _renderConditionValueInput(c, i, fld) {
    if (!fld) {
      return `<input type="text" value="${this._escapeAttr(c.value || "")}" class="text-[12px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
        data-action="change->workflow-builder#updateConditionValue" data-cond-index="${i}" />`
    }
    if (c.operator === "is_null" || c.operator === "is_not_null") {
      return `<span class="text-[11px] text-gray-400 italic flex-1">no value</span>`
    }
    if (fld.options) {
      return `<select class="text-[12px] border border-gray-200 rounded px-1.5 py-1 bg-white cursor-pointer flex-1"
                data-action="change->workflow-builder#updateConditionValue" data-cond-index="${i}">
        ${fld.options.map(o => `<option value="${this._escapeAttr(o.value)}" ${String(o.value) === String(c.value || "") ? "selected" : ""}>${this._escape(o.label)}</option>`).join("")}
      </select>`
    }
    if (fld.data_type === "currency" || fld.data_type === "number" || fld.data_type === "percent") {
      return `<input type="number" step="any" value="${this._escapeAttr(c.value ?? "")}" class="text-[12px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
        data-action="change->workflow-builder#updateConditionValue" data-cond-index="${i}" />`
    }
    return `<input type="text" value="${this._escapeAttr(c.value ?? "")}" class="text-[12px] border border-gray-200 rounded px-1.5 py-1 bg-white flex-1"
      data-action="change->workflow-builder#updateConditionValue" data-cond-index="${i}" />`
  }

  // ── Right-rail change handlers ─────────────────────────────────

  updateStepField(event) {
    const step = this._currentStep()
    if (!step) return
    const field = event.currentTarget.dataset.field
    let value = event.currentTarget.value
    if (event.currentTarget.type === "checkbox") value = event.currentTarget.checked
    if (field === "sla_business_hours" || field === "at_least_n") value = parseInt(value, 10) || 0
    step[field] = value
    if (field === "voting_rule") {
      // Show / hide N field
      this._renderRail()
    }
    this._renderCanvas()
  }

  updateEscalationKind(event) {
    const step = this._currentStep()
    if (!step) return
    const kind = event.currentTarget.value
    step.escalation = kind ? { kind: kind } : {}
    this._renderRail()
    this._renderCanvas()
  }

  updateEscalationField(event) {
    const step = this._currentStep()
    if (!step) return
    step.escalation = step.escalation || {}
    const field = event.currentTarget.dataset.field
    let value = event.currentTarget.value
    if (field === "notify_user_ids") {
      value = String(value).split(",").map(s => parseInt(s.trim(), 10)).filter(Boolean)
    } else if (field === "target_pool_id") {
      value = parseInt(value, 10) || null
    }
    step.escalation[field] = value
  }

  addCondition() {
    const step = this._currentStep()
    if (!step) return
    const fields = this._registry.condition_fields || []
    if (!fields.length) {
      this._flashRail("No condition fields available for this document type.", "error")
      return
    }
    const f = fields[0]
    step.conditions = step.conditions || []
    step.conditions.push({ field: f.key, operator: (f.operators || ["eq"])[0], value: "" })
    this._renderRail()
    this._renderCanvas()
  }

  removeCondition(event) {
    const step = this._currentStep()
    if (!step) return
    const i = parseInt(event.currentTarget.dataset.condIndex, 10)
    step.conditions.splice(i, 1)
    this._renderRail()
    this._renderCanvas()
  }

  updateConditionField(event) {
    const step = this._currentStep()
    if (!step) return
    const i = parseInt(event.currentTarget.dataset.condIndex, 10)
    const fields = this._registry.condition_fields || []
    const f = fields.find(x => x.key === event.currentTarget.value) || fields[0]
    step.conditions[i].field = f.key
    step.conditions[i].operator = (f.operators || ["eq"])[0]
    step.conditions[i].value = ""
    this._renderRail()
  }

  updateConditionOperator(event) {
    const step = this._currentStep()
    if (!step) return
    const i = parseInt(event.currentTarget.dataset.condIndex, 10)
    step.conditions[i].operator = event.currentTarget.value
    this._renderRail()
  }

  updateConditionValue(event) {
    const step = this._currentStep()
    if (!step) return
    const i = parseInt(event.currentTarget.dataset.condIndex, 10)
    step.conditions[i].value = event.currentTarget.value
  }

  // ── Pool picker bridge ─────────────────────────────────────────

  openPoolPicker() {
    document.dispatchEvent(new CustomEvent("pool-picker:open", {
      detail: { workflowId: this.workflowIdValue, stepId: this._selectedStepId }
    }))
  }

  _onPoolPicked(event) {
    const step = this._currentStep()
    if (!step) return
    if (event.detail?.stepId && event.detail.stepId !== this._selectedStepId) return
    step.approver_pool_id = event.detail.poolId
    // Update local pool catalog if a brand-new pool came back
    if (event.detail.pool && !this._registry.pools.find(p => p.id === event.detail.pool.id)) {
      this._registry.pools.push(event.detail.pool)
    }
    this._renderRail()
    this._renderCanvas()
  }

  // ── Save step (PATCH) + preview ────────────────────────────────

  async saveStep() {
    const step = this._currentStep()
    if (!step) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const url = this.stepsUrlValue + "/" + step.id
    const r = await fetch(url, {
      method: "PATCH",
      headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrf || "" },
      body: JSON.stringify({ approval_step: {
        name: step.name,
        position: step.position,
        voting_rule: step.voting_rule,
        at_least_n: step.at_least_n,
        sla_business_hours: step.sla_business_hours,
        approver_pool_id: step.approver_pool_id,
        allow_email_approval: step.allow_email_approval,
        require_comment_on_reject: step.require_comment_on_reject,
        conditions: step.conditions || [],
        escalation: step.escalation || {}
      } })
    })
    const j = await r.json().catch(() => ({}))
    if (j.success && j.step) {
      // Keep the local row in sync with what the server stored.
      Object.assign(step, j.step)
      this._renderCanvas()
      this._schedulePreview(0)
      this._flashRail("Saved")
    } else {
      this._flashRail((j.errors || ["Failed to save step"]).join(", "), "error")
    }
  }

  // ── Preview ────────────────────────────────────────────────────

  previewInputChanged() {
    this._schedulePreview()
  }

  _schedulePreview(delay = 600) {
    if (!this.hasPreviewBodyTarget || !this.previewUrlValue) return
    if (this._previewTimer) clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this._runPreview(), delay)
  }

  async _runPreview() {
    if (!this.previewUrlValue) return
    const metadata = this._collectPreviewInputs()
    this.previewBodyTarget.innerHTML = `<div class="text-[12px] text-gray-400">Evaluating…</div>`
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const r = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrf || "" },
        body: JSON.stringify({ metadata })
      })
      const j = await r.json()
      this._renderPreview(j)
    } catch (e) {
      this.previewBodyTarget.innerHTML = `<div class="text-[12px] text-rose-700">Preview failed.</div>`
    }
  }

  _collectPreviewInputs() {
    if (!this.hasPreviewInputsTarget) return {}
    const inputs = this.previewInputsTarget.querySelectorAll("[data-preview-field]")
    const meta = {}
    inputs.forEach(el => {
      const k = el.dataset.previewField
      const v = el.value
      if (v !== "") meta[k] = (el.type === "number") ? parseFloat(v) : v
    })
    return meta
  }

  _renderPreview(payload) {
    if (!payload || !payload.success) {
      this.previewBodyTarget.innerHTML = `<div class="text-[12px] text-rose-700">Preview failed.</div>`
      return
    }

    if (!payload.steps.length) {
      this.previewBodyTarget.innerHTML = `<div class="text-[12px] text-gray-500">No steps yet — preview is empty.</div>`
      return
    }

    const rows = payload.steps.map(s => {
      const skipped = s.condition_status === "skipped"
      const banner = skipped ? `<span class="ml-1 text-rose-600 text-[10px]">SKIPPED</span>` : ""
      const poolLine = s.pool && (s.pool.name || s.pool.kind)
        ? `${this._escape(s.pool.name || s.pool.kind)} → ${s.pool.resolved_size} approver${s.pool.resolved_size === 1 ? "" : "s"}`
        : "<em>no pool</em>"
      const names = (s.pool?.resolved_user_names || []).slice(0, 4).join(", ")
      const namesLine = names ? `<div class="text-[10px] text-gray-400 truncate">${this._escape(names)}</div>` : ""
      const sla = s.sla_business_hours > 0 ? ` · SLA ${s.sla_business_hours}h` : ""
      const cond = (s.condition_summary || []).join(" AND ")
      const condLine = cond ? `<div class="text-[10px] text-gray-400">if ${this._escape(cond)}</div>` : ""
      return `
        <li class="${skipped ? "opacity-60" : ""}">
          <div class="flex items-start gap-2">
            <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-[10px] font-semibold text-gray-700 flex-shrink-0">${s.position}</span>
            <div class="min-w-0">
              <div class="text-[12px] text-gray-900 font-medium">${this._escape(s.name)} ${banner}</div>
              <div class="text-[11px] text-gray-600">${poolLine} · ${this._humanize(s.voting_rule)}${sla}</div>
              ${namesLine}${condLine}
            </div>
          </div>
        </li>`
    }).join("")
    const final = payload.final_status === "would_skip_to_complete"
      ? `<div class="text-[11px] text-amber-700 mt-2 italic">All steps would be skipped → request would auto-complete.</div>`
      : ""
    this.previewBodyTarget.innerHTML = `<ol class="space-y-2">${rows}</ol>${final}`
  }

  // ── Helpers ────────────────────────────────────────────────────

  _currentStep() {
    return this._steps.find(s => s.id === this._selectedStepId)
  }

  _poolName(id) {
    const p = (this._registry.pools || []).find(x => x.id === id)
    return p ? this._escape(p.name) : `Pool #${id}`
  }

  _humanize(s) {
    if (!s) return ""
    return String(s).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  _operatorLabel(op) {
    return ({
      eq: "equals", neq: "≠", lt: "<", lte: "≤", gt: ">", gte: "≥",
      in: "in", not_in: "not in", contains: "contains",
      between: "between", is_null: "is empty", is_not_null: "is set"
    })[op] || op
  }

  _flashRail(msg, kind = "success") {
    const cls = kind === "error"
      ? "bg-rose-600 text-white"
      : "bg-emerald-600 text-white"
    const flash = document.createElement("div")
    flash.className = `fixed top-6 right-6 ${cls} text-[12px] px-3 py-1.5 rounded-lg shadow-lg z-50`
    flash.textContent = msg
    document.body.appendChild(flash)
    setTimeout(() => flash.remove(), kind === "error" ? 4000 : 1500)
  }

  _escape(str) {
    if (str === null || str === undefined) return ""
    return String(str).replace(/[&<>"']/g, m => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;"
    })[m])
  }

  _escapeAttr(str) {
    return this._escape(str)
  }
}
